import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import VideoCard from '@/components/base/VideoCard';
import FilterPanel, { type FilterState } from '@/pages/category/components/FilterPanel';
import { mainCategories } from '@/mocks/categories';
import { videoItems } from '@/mocks/videos';

const PAGE_SIZE = 8;

const defaultFilters: FilterState = { type: '全部', region: '全部', year: '全部' };

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const category = mainCategories.find((c) => c.slug === type);

  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const list = useMemo(() => {
    const base = videoItems.filter((v) => v.categorySlug === type);
    const filtered = base.filter((v) => {
      if (filters.type !== '全部' && v.type !== filters.type) return false;
      if (filters.region !== '全部' && v.region !== filters.region) return false;
      if (filters.year !== '全部' && String(v.year) !== filters.year) return false;
      return true;
    });
    const sorted = [...filtered];
    if (sort === 'hot') {
      sorted.sort((a, b) => parseFloat(b.views.replace(/[^\d.]/g, '')) - parseFloat(a.views.replace(/[^\d.]/g, '')));
    } else if (sort === 'rating') {
      sorted.sort((a, b) => b.rating - a.rating);
    } else {
      sorted.sort((a, b) => (a.uploadDate < b.uploadDate ? 1 : -1));
    }
    return sorted;
  }, [type, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleChange = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setSort('latest');
    setPage(1);
  };

  if (!category) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-4xl text-foreground-300" />
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">未找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的分类不存在，请返回首页重新选择。</p>
        <Link
          to="/"
          className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-5-line" />
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6">
      <nav className="flex items-center gap-2 text-sm text-foreground-400">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-700">{category.name}分类</span>
      </nav>

      <header className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
            {category.name}分类
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-500">{category.desc}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-background-100 px-3 py-1.5 text-sm text-foreground-500">
            共 <strong className="text-primary-600">{list.length}</strong> 条内容
          </span>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm font-medium text-foreground-700 lg:hidden"
          >
            <i className="ri-filter-3-line" />
            筛选
          </button>
        </div>
      </header>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200/70 bg-background-50 p-5">
            <FilterPanel
              filters={filters}
              onChange={handleChange}
              sort={sort}
              onSortChange={(v) => {
                setSort(v);
                setPage(1);
              }}
              onReset={handleReset}
            />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          {pageItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <i className="ri-inbox-archive-line text-3xl text-foreground-300" />
              <p className="mt-3 text-sm text-foreground-500">暂无相关内容，尝试重置筛选</p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-5 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {pageItems.map((item) => (
                <VideoCard key={item.id} item={item} />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i + 1)}
                  className={`h-9 w-9 rounded-md text-sm font-medium transition-colors ${
                    currentPage === i + 1
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-background-950/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-y-0 right-0 flex w-full max-w-sm flex-col overflow-y-auto bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-heading text-lg font-bold text-foreground-900">筛选</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭筛选"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-600"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterPanel
              filters={filters}
              onChange={handleChange}
              sort={sort}
              onSortChange={(v) => {
                setSort(v);
                setPage(1);
              }}
              onReset={handleReset}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看 {list.length} 条结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}